import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/cre/Header";
import Footer from "@/components/cre/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ShieldCheck, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const tooShort = pw && pw.length < 8;
  const mismatch = pw && pw2 && pw !== pw2;
  const submitDisabled = busy || !pw || !pw2 || tooShort || mismatch || !token;

  const submit = async (e) => {
    e.preventDefault();
    if (submitDisabled) return;
    setBusy(true);
    setError("");
    try {
      await api.confirmPasswordReset(token, pw);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "This reset link is invalid or has expired. Please request a new one."
      );
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Header />
        <div className="flex-1 max-w-md mx-auto w-full px-6 md:px-8 py-14 md:py-20">
          <div
            data-testid="reset-no-token-state"
            className="cre-card p-6 text-center"
          >
            <AlertTriangle className="h-8 w-8 text-amber-600 mx-auto" />
            <h1 className="font-heading text-xl font-bold mt-3 text-slate-900">
              No reset token found
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              This page expects a reset link sent to your email. Please use
              the link in your email, or request a new one.
            </p>
            <div className="mt-5">
              <Link to="/forgot-password">
                <Button className="cre-cta-primary">Request a new link</Button>
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Header />
      <div className="flex-1">
        <div className="max-w-md mx-auto px-6 md:px-8 py-14 md:py-20">
          <div className="cre-overline text-sky-700">Account · recovery</div>
          <h1 className="font-heading text-3xl font-black tracking-tight mt-1 text-slate-900">
            Set a new password
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Choose a strong password — at least 8 characters. After saving,
            you'll be able to sign in with the new credentials.
          </p>

          {done ? (
            <div
              data-testid="reset-success-state"
              className="cre-card p-6 mt-8 text-center"
            >
              <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
              <h2 className="font-heading text-xl font-bold mt-3 text-slate-900">
                Password updated
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                Your password has been changed. You can now sign in with the
                new password.
              </p>
              <div className="mt-5">
                <Button
                  data-testid="reset-goto-login"
                  onClick={() => navigate("/login", { replace: true })}
                  className="cre-cta-primary"
                >
                  Sign in
                </Button>
              </div>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="cre-card p-6 mt-8 space-y-4"
              data-testid="reset-password-form"
            >
              <div>
                <Label className="cre-overline">New password</Label>
                <Input
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  data-testid="reset-password-new"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="mt-1.5 h-11 bg-white border-slate-200 focus-visible:ring-sky-500"
                />
                {tooShort ? (
                  <p className="mt-1 text-[11px] text-amber-700">
                    At least 8 characters.
                  </p>
                ) : null}
              </div>
              <div>
                <Label className="cre-overline">Confirm new password</Label>
                <Input
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  data-testid="reset-password-confirm"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  className="mt-1.5 h-11 bg-white border-slate-200 focus-visible:ring-sky-500"
                />
                {mismatch ? (
                  <p className="mt-1 text-[11px] text-rose-700">
                    Passwords don't match.
                  </p>
                ) : null}
              </div>

              {error ? (
                <div
                  data-testid="reset-error"
                  className="text-xs text-rose-700 border border-rose-200 bg-rose-50 rounded-md px-3 py-2"
                >
                  {error}
                </div>
              ) : null}

              <Button
                type="submit"
                disabled={submitDisabled}
                data-testid="reset-submit"
                className="w-full cre-cta-primary rounded-lg font-semibold h-11"
              >
                <KeyRound className="h-4 w-4 mr-2" strokeWidth={2} />
                {busy ? "Saving…" : "Save new password"}
              </Button>
              <div className="text-center text-sm text-slate-600">
                <Link
                  to="/login"
                  className="text-sky-700 hover:text-sky-600 underline-offset-4 hover:underline font-semibold"
                >
                  Back to sign in
                </Link>
              </div>
            </form>
          )}

          <div className="mt-4 flex items-start gap-2 text-[12px] text-slate-500">
            <ShieldCheck
              className="h-3.5 w-3.5 text-emerald-600 mt-0.5"
              strokeWidth={2}
            />
            <p>
              Reset links are single-use and expire after 30 minutes. After
              saving, every other outstanding reset link for this account is
              invalidated.
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
